import { useEffect, useRef } from 'react';

import { bindAll } from 'bind-event-listener';

import traceUFOInteraction from '@atlaskit/react-ufo/trace-interaction';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';

import { QUICK_FIND_CLOSE_INTERACTION } from '../../../common/constants/quick-find';
import { serve3PBulkConnectWithoutExposure } from '../../../common/utils/experiments';
import {
	useIsBulkConnectModalOpen,
	useIsFeedbackModalOpen,
	useQuickFindActions,
	useQuickFindOpen,
} from '../../../controllers/store/quick-find';

const isClickInRelatedLinkModalBlanket = (targetNode: Node | null): boolean => {
	if (!targetNode) {
		return false;
	}

	const relatedLinksModal = document.querySelector('[data-testid="related-links-modal"]');
	const relatedLinkModalBlanket = document.querySelector(
		'[data-testid="related-links-modal--blanket"]',
	);

	const isInRelatedLinksModal = relatedLinksModal ? relatedLinksModal.contains(targetNode) : false;
	const isInRelatedLinkModalBlanket = relatedLinkModalBlanket
		? relatedLinkModalBlanket.contains(targetNode)
		: false;

	return isInRelatedLinksModal || isInRelatedLinkModalBlanket;
};

export const useCloseManager = ({
	dialogRef,
	inputBoxRef,
}: {
	dialogRef: React.RefObject<HTMLDivElement>;
	inputBoxRef: React.RefObject<HTMLDivElement>;
}): void => {
	const mouseDownInside = useRef(false);

	const quickFindOpen = useQuickFindOpen();

	const [isFeedbackModalOpen] = useIsFeedbackModalOpen();
	const [isBulkConnectModalOpen] = useIsBulkConnectModalOpen();
	const { setQuickFindOpen } = useQuickFindActions();

	useEffect(() => {
		const onMouseDown = (event: MouseEvent | React.MouseEvent) => {
			mouseDownInside.current = false;

			const { target } = event;
			const targetNode = target as Node | null;

			const isClickOnPopup = dialogRef.current && dialogRef.current.contains(targetNode);
			const isClickOnTrigger = inputBoxRef.current && inputBoxRef.current.contains(targetNode);

			if (isClickOnPopup || isClickOnTrigger) {
				mouseDownInside.current = true;
			}
		};

		const onClick = (event: MouseEvent | React.MouseEvent) => {
			const { target } = event;
			const targetNode = target as Node | null;

			if (isFeedbackModalOpen) {
				return;
			}

			if (isBulkConnectModalOpen && serve3PBulkConnectWithoutExposure()) {
				return;
			}

			if (isClickInRelatedLinkModalBlanket(targetNode)) {
				return;
			}

			const isClickOnPopup = dialogRef.current && dialogRef.current.contains(targetNode);
			const isClickOnTrigger = inputBoxRef.current && inputBoxRef.current.contains(targetNode);

			if (!isClickOnPopup && !isClickOnTrigger && !mouseDownInside.current) {
				setQuickFindOpen(false);

				if (quickFindOpen) {
					traceUFOInteraction(QUICK_FIND_CLOSE_INTERACTION, event as React.MouseEvent);
				}
			}
		};

		const onKeyDown = (event: KeyboardEvent | React.KeyboardEvent) => {
			const { key } = event;
			if (key === 'Escape' || key === 'Esc') {
				setQuickFindOpen(false);

				if (quickFindOpen) {
					traceUFOInteraction(QUICK_FIND_CLOSE_INTERACTION, event as React.KeyboardEvent);
				}
			}
		};

		const onBlur = () => {
			const activeElement = document.activeElement;
			const activeElementIsIFrame = activeElement instanceof HTMLIFrameElement;

			// Close Quick Find if the new active element is an iframe, we need
			// to do this check since click events are not fired on iframes
			if (activeElementIsIFrame) {
				setQuickFindOpen(false);

				if (quickFindOpen) {
					traceUFOPress(QUICK_FIND_CLOSE_INTERACTION, performance.now());
				}
			}
		};

		const unbind = bindAll(window, [
			{
				type: 'mousedown',
				listener: onMouseDown,
				options: { capture: false },
			},
			{
				type: 'click',
				listener: onClick,
				options: {
					capture: true, // Closes the dialog before SPA transitions
				},
			},
			{
				type: 'keydown',
				listener: onKeyDown,
			},
			{
				type: 'blur',
				listener: onBlur,
			},
		]);

		return () => {
			unbind();
		};
	}, [
		setQuickFindOpen,
		isFeedbackModalOpen,
		isBulkConnectModalOpen,
		quickFindOpen,
		dialogRef,
		inputBoxRef,
	]);
};
